import type { ReactNode } from 'react';
import { FormHeader } from './FormHeader';
import { INTAKE_CARD_COLUMN, INTAKE_COLUMNS, INTAKE_SHELL } from './layout';
import type { SaveStatus } from './SaveIndicator';
import { SectionRail } from './SectionRail';
import type { MarkWords, RailSection } from './types';

/**
 * The page both the intake and the onboarding form sit in (crossmarc/steward-app#1886): the
 * sticky {@link FormHeader}, then a two-track body with the {@link SectionRail} in the first
 * track from `lg` and the card (`children`) in the second. Below `lg` the same rail is in the
 * header's sheet, with `sheetFooter` under it.
 *
 * "Section N of M" counts the numbered rows only; on a Review row the phone button says
 * "Review". The hairline counts every row, Review included.
 *
 * The intake's own copy of these pieces (IntakeHeader, IntakeSectionList, now removed) is gone; this is
 * the one place a change to the chrome is made.
 */
export function FormShell({
  mark, suffix, save, onRetry, right, sheetFooter, sections, currentKey, words, onSelect, disabled, showStarted, railHeader, railFooter, stepLabel, children,
}: {
  mark: ReactNode;
  suffix?: string;
  save: SaveStatus;
  onRetry: () => void;
  right?: ReactNode;
  sheetFooter?: ReactNode;
  sections: readonly RailSection[];
  currentKey: string;
  words: MarkWords;
  onSelect: (key: string) => void;
  disabled?: boolean;
  /** Draw a ring on a `started` row (see `SectionRail`). The onboarding form sets it. */
  showStarted?: boolean;
  /** Shown above the rail's first row from `lg` up, and not in the phone's sheet. */
  railHeader?: ReactNode;
  /** Shown under the rail from `lg` up, and not in the phone's sheet. */
  railFooter?: ReactNode;
  /**
   * The phone button's text while the current row is an unnumbered one that is not Review (no
   * caller passes it now that the intake's tax returns are a numbered section). It replaces "Review" or "Section N of M" and changes
   * nothing else: which row is current is still `currentKey`.
   */
  stepLabel?: string;
  children: ReactNode;
}) {
  const numbered = sections.filter((s) => !s.review);
  const currentIndex = sections.findIndex((s) => s.key === currentKey);
  const current = sections[currentIndex];
  const positionLabel = stepLabel ?? (current?.review ? 'Review' : `Section ${numbered.findIndex((s) => s.key === currentKey) + 1} of ${numbered.length}`);
  return (
    <>
      <FormHeader
        mark={mark} suffix={suffix} save={save} onRetry={onRetry} right={right} sheetFooter={sheetFooter}
        positionLabel={positionLabel} position={currentIndex + 1} total={sections.length} sheetKey={currentKey}
        sheet={(close) => (
          <SectionRail sections={sections} currentKey={currentKey} words={words} disabled={disabled} showStarted={showStarted}
            onSelect={(key) => { close(); onSelect(key); }} />
        )}
      />
      <main className={`${INTAKE_SHELL} ${INTAKE_COLUMNS} pb-16 pt-8 sm:pt-10`} data-testid="form-shell-main">
        {/* The header card is a sibling above the nav, so it is not inside the navigation landmark. */}
        <div className="hidden lg:sticky lg:top-20 lg:block lg:max-h-[calc(100dvh-5rem)] lg:self-start lg:overflow-y-auto">
          {railHeader}
          <nav aria-label="Form sections">
            <SectionRail sections={sections} currentKey={currentKey} words={words} onSelect={onSelect} disabled={disabled} showStarted={showStarted} />
            {railFooter}
          </nav>
        </div>
        <div className={INTAKE_CARD_COLUMN}>{children}</div>
      </main>
    </>
  );
}
